/**
 * "O que fazer agora" do lead: ate tres sugestoes derivadas da situacao, da
 * janela de comissao e do que a pessoa fez (leu, clicou, respondeu).
 *
 * Um botao por sugestao, sempre `secondary`: o unico `primary` da coluna e
 * "Marcar venda" no cartao de acoes, e a sugestao que pede isso so rola ate
 * la (ou abre o modal pelo mesmo callback). Acoes exclusivas da
 * administracao (mensagem manual) viram uma linha explicativa para o
 * lojista, porque disparos sao operados pela equipe do Converte Facil.
 */

import { Button, Footnote, Icon, LinkButton, Panel, SectionCard, type BadgeTone, type IconName } from '../../../components/ui';
import type { AcaoDaSugestao, SugestaoDoLead, TomDaSugestao } from '../resumo';

const TOM: Readonly<Record<TomDaSugestao, { readonly tone: BadgeTone; readonly painel: 'surface' | 'good' | 'warning' | 'accent'; readonly icone: IconName }>> = {
  alerta: { tone: 'warning', painel: 'warning', icone: 'alerta' },
  oportunidade: { tone: 'good', painel: 'good', icone: 'raio' },
  info: { tone: 'info', painel: 'surface', icone: 'info' },
  bom: { tone: 'good', painel: 'surface', icone: 'check' },
};

const ICONE_TOM: Readonly<Record<BadgeTone, string>> = {
  neutral: 'bg-ink/5 text-ink-muted',
  brand: 'bg-brand/10 text-brand',
  accent: 'bg-accent/10 text-accent',
  info: 'bg-info/10 text-info',
  good: 'bg-status-good/10 text-status-good',
  warning: 'bg-status-warning/15 text-status-warning',
  critical: 'bg-status-critical/10 text-status-critical',
  violet: 'bg-series-7/10 text-series-7',
};

export function SugestoesDoLead({
  sugestoes,
  isPlatformAdmin,
  onMarcarVenda,
}: {
  readonly sugestoes: readonly SugestaoDoLead[];
  readonly isPlatformAdmin: boolean;
  readonly onMarcarVenda: () => void;
}): JSX.Element {
  const executar = (acao: AcaoDaSugestao): void => {
    if (acao.kind === 'marcar_venda') {
      onMarcarVenda();
      return;
    }
    if (acao.kind === 'rolar') {
      document.getElementById(acao.alvo)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <SectionCard id="sugestoes-do-lead" title="O que fazer agora" icon="clique" description="Lido da linha do tempo e da janela de comissão deste lead.">
      {sugestoes.length === 0 ? (
        <p className="rounded-xl border border-dashed border-edge px-4 py-5 text-center text-sm text-ink-subtle">Nada a fazer agora: o motor cuida deste lead sozinho.</p>
      ) : (
        <ol className="flex flex-col gap-3">
          {sugestoes.map((s) => {
            const t = TOM[s.tom];
            return (
              <li key={s.key}>
                <Panel tone={t.painel} className="flex flex-col gap-2 px-4 py-3">
                  <div className="flex items-start gap-3">
                    <span aria-hidden="true" className={`mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg ${ICONE_TOM[t.tone]}`}>
                      <Icon name={t.icone} size="sm" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold leading-snug text-ink">{s.titulo}</h3>
                      <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">{s.texto}</p>
                    </div>
                  </div>
                  {s.acao !== null && s.rotuloAcao !== null ? (
                    s.acao.kind === 'rota' ? (
                      s.acao.adminOnly && !isPlatformAdmin ? (
                        <Footnote>Mensagens manuais são enviadas pela equipe do Converte Fácil em nome da sua empresa. Se quiser, avise o seu contato.</Footnote>
                      ) : (
                        <div>
                          <LinkButton to={s.acao.to} variant="secondary" size="sm" iconRight="seta-dir">
                            {s.rotuloAcao}
                          </LinkButton>
                        </div>
                      )
                    ) : (
                      <div>
                        <Button variant="secondary" size="sm" iconRight="seta-dir" onClick={() => (s.acao === null ? undefined : executar(s.acao))}>
                          {s.rotuloAcao}
                        </Button>
                      </div>
                    )
                  ) : null}
                </Panel>
              </li>
            );
          })}
        </ol>
      )}
    </SectionCard>
  );
}
